import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import apiUrls from "../../../api";
import type { ScopeType } from "../types";
import SearchableSelect from "./SearchableSelect";

interface ScopePickerProps {
  value: { scope_type: ScopeType; scope_id: number } | null;
  onChange: (next: { scope_type: ScopeType; scope_id: number } | null) => void;
}

interface PgWithProjects {
  project_group_id: number;
  project_group_name: string;
  projects: Array<{ project_id: number; project_name: string }>;
}

/**
 * Cascading searchable selects: project_group (required) → project
 * (optional, "All modules in this course" by default).
 *
 * The native <select> we used previously becomes unusable past ~50 options.
 * `SearchableSelect` gives us instant filtering for tenants with hundreds of
 * project groups.
 */
const ScopePicker = ({ value, onChange }: ScopePickerProps) => {
  const { t } = useTranslation();
  const [pgList, setPgList] = useState<PgWithProjects[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchPgs = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(apiUrls.stats.getAllSessionsByProjectGroup);
        if (cancelled) return;
        const raw = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
        const mapped: PgWithProjects[] = raw.map((pg: any) => ({
          project_group_id: pg.project_group_id,
          project_group_name: pg.project_group_name,
          projects: Array.isArray(pg.projects)
            ? pg.projects.map((p: any) => ({
                project_id: p.project_id,
                project_name: p.project_name,
              }))
            : [],
        }));
        // Sort PGs alphabetically — common UX expectation for searchable lists.
        mapped.sort((a, b) => a.project_group_name.localeCompare(b.project_group_name));
        setPgList(mapped);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message ?? "fetch failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPgs();
    return () => {
      cancelled = true;
    };
  }, []);

  // Selected PG id — derived from current scope:
  // - project_group scope → scope_id is the PG id directly
  // - project scope → look up the owning PG
  const selectedPgId = useMemo<string | null>(() => {
    if (!value) return null;
    if (value.scope_type === "project_group") return String(value.scope_id);
    const owning = pgList.find((pg) => pg.projects.some((p) => p.project_id === value.scope_id));
    return owning ? String(owning.project_group_id) : null;
  }, [value, pgList]);

  const selectedPg = pgList.find((pg) => String(pg.project_group_id) === selectedPgId);

  const pgOptions = useMemo(
    () =>
      pgList.map((pg) => ({
        value: String(pg.project_group_id),
        label: pg.project_group_name,
      })),
    [pgList],
  );

  const projectOptions = useMemo(
    () =>
      (selectedPg?.projects ?? [])
        .slice()
        .sort((a, b) => a.project_name.localeCompare(b.project_name))
        .map((p) => ({
          value: String(p.project_id),
          label: p.project_name,
        })),
    [selectedPg],
  );

  const handlePgChange = (next: string | null) => {
    if (!next) {
      onChange(null);
      return;
    }
    onChange({ scope_type: "project_group", scope_id: Number(next) });
  };

  const handleProjectChange = (next: string | null) => {
    if (!selectedPg) return;
    if (!next) {
      onChange({ scope_type: "project_group", scope_id: selectedPg.project_group_id });
      return;
    }
    onChange({ scope_type: "project", scope_id: Number(next) });
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-gray-700">
          {t("pages.statistics.reports.fields.projectGroup")}
        </label>
        <SearchableSelect
          options={pgOptions}
          value={selectedPgId}
          onChange={handlePgChange}
          placeholder={
            loading
              ? t("general.loading")
              : t("pages.statistics.reports.fields.projectGroupPlaceholder")
          }
          disabled={loading}
        />
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>

      {selectedPg && (
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-700">
            {t("pages.statistics.reports.fields.project")}
          </label>
          <SearchableSelect
            options={projectOptions}
            value={value?.scope_type === "project" ? String(value.scope_id) : null}
            onChange={handleProjectChange}
            placeholder={t("pages.statistics.reports.fields.projectAll")}
            emptyOptionLabel={t("pages.statistics.reports.fields.projectAll")}
          />
        </div>
      )}
    </div>
  );
};

export default ScopePicker;
